import { createContext } from 'react';
import type {
  AppSettings,
  Project,
  ProjectInput,
  ProjectWire,
  SectionReply,
  Organization,
  GeneratedSection,
} from '../types';
import { wireToProject, projectToBlob } from '../types';
import { sectionOrder, type SectionKey, getSectionLabels } from '../prompts';
import type { Locale } from '../i18n';

export interface AppContextValue {
  // Projects
  projects: Project[];
  activeProjectId: string | null;
  setActiveProjectId: (id: string | null) => void;
  createProject: (name: string, input: ProjectInput) => Promise<Project>;
  updateProject: (project: Project) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  updateSection: (
    projectId: string,
    key: SectionKey,
    content: string,
    status: GeneratedSection['status'],
    error?: string
  ) => Promise<void>;
  addSectionReply: (projectId: string, key: SectionKey, reply: SectionReply) => Promise<void>;
  refreshProjects: () => Promise<void>;

  // Active interview (still local — session hint, not data)
  activeInterviewSessionId: string | null;
  setActiveInterviewSessionId: (id: string | null) => void;
  legacyMigrationPending: string[];
  markLegacyMigrationDone: () => void;

  // Settings
  settings: AppSettings;
  setSettings: (settings: AppSettings) => void;

  // Loading state
  loadingProjects: boolean;
}

export const AppContext = createContext<AppContextValue | null>(null);

export function makeEmptySections(locale: Locale = 'en'): Record<SectionKey, GeneratedSection> {
  const labels = getSectionLabels(locale);
  const placeholder =
    locale === 'de'
      ? 'Klicke auf **Generieren**, um diesen Abschnitt basierend auf deinen Projektdaten zu erstellen.'
      : 'Click **Generate** to create this section based on your project input.';
  return sectionOrder.reduce((acc, key) => {
    acc[key] = {
      key,
      content: `## ${labels[key]}\n\n${placeholder}`,
      status: 'idle',
    };
    return acc;
  }, {} as Record<SectionKey, GeneratedSection>);
}

export type { Project, ProjectInput, ProjectWire, Organization, SectionReply, GeneratedSection };

// Helpers
export async function fetchProjectsWire(): Promise<ProjectWire[]> {
  const res = await fetch('/api/projects');
  if (!res.ok) return [];
  const data = (await res.json()) as { projects: ProjectWire[] };
  return data.projects || [];
}

export async function fetchProjectWire(id: string): Promise<ProjectWire | null> {
  const res = await fetch(`/api/projects/${id}`);
  if (!res.ok) return null;
  const data = (await res.json()) as { project: ProjectWire };
  return data.project;
}

export function wireToProjectSafe(wire: ProjectWire): Project {
  return wireToProject(wire);
}

export function projectToBlobSafe(p: Project): string {
  return projectToBlob(p);
}
